/**
 * The BlueView brand mark.
 *
 * A single SVG so it stays crisp at any size and renders identically wherever
 * it is placed — no font metrics or per-site padding to drift out of sync.
 *
 * Deliberately typographic: the wordmark plus the pill and its diagonal split,
 * with no globe, pin, or emblem. The only illustrative element is the very low
 * contrast map texture behind "View", which reads as a background, not a logo.
 *
 * FIXED BRAND ELEMENT: like the constants it replaces, this is not driven by
 * HomePageConfig. No role can restyle or remove it through Settings.
 */

interface Props {
  /** Rendered height in px; width follows the 3.6:1 lockup ratio. */
  height?: number;
  className?: string;
}

export default function BlueViewMark({ height = 46, className = '' }: Props) {
  const FONT =
    "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif";

  return (
    <svg
      viewBox="0 0 720 200"
      height={height}
      width={height * 3.6}
      role="img"
      aria-label="BlueView"
      className={className}
      style={{
        display: 'block',
        // Depth only — no coloured halo. The mark identifies the product; it
        // should sit behind the portal title and the client's own logo, not
        // compete with them.
        filter: 'drop-shadow(0 2px 6px rgba(3,12,28,0.45))',
      }}
    >
      <defs>
        {/* Pill interior — the light half */}
        <linearGradient id="bv-light" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#EEF4FB" />
          <stop offset="100%" stopColor="#DCE7F4" />
        </linearGradient>

        {/* Pill interior — the navy half */}
        <linearGradient id="bv-navy" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0B3A8F" />
          <stop offset="55%" stopColor="#082A64" />
          <stop offset="100%" stopColor="#061C42" />
        </linearGradient>

        {/* "Blue" wordmark */}
        <linearGradient id="bv-word" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="52%" stopColor="#DCEBFF" />
          <stop offset="100%" stopColor="#7FBCFF" />
        </linearGradient>

        {/* The V */}
        <linearGradient id="bv-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2FA8F5" />
          <stop offset="50%" stopColor="#1D5FD0" />
          <stop offset="100%" stopColor="#0E2C6B" />
        </linearGradient>

        {/* Faint halftone that reads as a world map behind "View" */}
        <pattern id="bv-dots" width="9" height="9" patternUnits="userSpaceOnUse">
          <circle cx="1.6" cy="1.6" r="1.5" fill="#0B2A5B" />
        </pattern>

        {/* Everything is clipped to the pill so the diagonal split cannot spill */}
        <clipPath id="bv-pill">
          <rect x="8" y="8" width="704" height="184" rx="92" />
        </clipPath>
      </defs>

      <g clipPath="url(#bv-pill)">
        {/* Light half */}
        <rect x="0" y="0" width="720" height="200" fill="url(#bv-light)" />

        {/* Map texture, kept very low contrast so the wordmark stays dominant */}
        <g opacity="0.15">
          <rect x="470" y="26" width="240" height="150" fill="url(#bv-dots)" />
        </g>
        <g stroke="#0B2A5B" strokeWidth="1.4" fill="none" opacity="0.12">
          <path d="M400 160 C 452 140, 492 178, 542 152 S 622 130, 716 148" />
          <path d="M400 178 C 456 158, 498 194, 548 170 S 624 150, 716 166" />
          <path d="M430 42 C 478 24, 518 58, 566 36 S 638 18, 716 32" />
        </g>

        {/* Navy half with the diagonal split */}
        <path d="M0 0 L400 0 L352 200 L0 200 Z" fill="url(#bv-navy)" />
        {/* Bright edge along the split */}
        <path d="M400 0 L352 200" stroke="rgba(150,195,240,0.35)" strokeWidth="2" fill="none" />

        {/* ── "Blue" ────────────────────────────────────────────────────── */}
        <text
          x="77"
          y="138"
          fill="url(#bv-word)"
          fontSize="106"
          fontWeight={800}
          letterSpacing="-2"
          fontFamily={FONT}
        >
          Blue
        </text>

        {/* ── "View" — the V is a shape so it can carry the gradient, the
               rest is text on the same baseline ─────────────────────────── */}
        <path
          d="M397 58 L439 138 L481 58"
          fill="none"
          stroke="url(#bv-accent)"
          strokeWidth="32"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <text
          x="495"
          y="138"
          fill="#0E2C6B"
          fontSize="106"
          fontWeight={800}
          letterSpacing="-2"
          fontFamily={FONT}
        >
          iew
        </text>
      </g>

      {/* Pill outline, drawn last so it sits cleanly over the split */}
      <rect
        x="8"
        y="8"
        width="704"
        height="184"
        rx="92"
        fill="none"
        stroke="rgba(120,170,235,0.55)"
        strokeWidth="5"
      />
      <rect
        x="14"
        y="14"
        width="692"
        height="172"
        rx="86"
        fill="none"
        stroke="rgba(255,255,255,0.22)"
        strokeWidth="1.5"
      />
    </svg>
  );
}
